import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/**
 * PrintWorx3D cart behaviours.
 *
 * 1. "Added to cart" status in the drawer header, driven only by Shopify's cart line
 *    update event with action "add" (never a timer on page load).
 * 2. <pw-cart-recs>: swaps the server-rendered fallback recommendations for Shopify's
 *    related products, fetched through the Section Rendering API.
 * 3. <pw-cart-sticky>: mobile checkout bar on the cart page, shown only while the order
 *    summary's checkout button is off screen.
 * 4. Build Your Own Clicker helper lines: keeps the hidden R0 cap lines in step with the kits
 *    in the cart (see the block at the end of this file).
 */

const ADDED_VISIBLE_MS = 4000;

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.action !== 'add') return;
  const show = () => {
    // The flag lives on the <theme-drawer> root (data-skip-node-update), which the Section
    // Rendering morph leaves alone, so the status survives the re-render that follows the add.
    const drawer = document.getElementById('cart-drawer');
    if (!drawer) return;
    drawer.setAttribute('data-pw-added', '');
    clearTimeout(drawer._pwAddedTimer);
    drawer._pwAddedTimer = setTimeout(() => {
      drawer.removeAttribute('data-pw-added');
    }, ADDED_VISIBLE_MS);
  };
  if (event.promise) {
    event.promise.then(({ detail }) => {
      if (!detail?.didError) show();
    }).catch(() => {});
  } else {
    show();
  }
});

const recsCache = new Map();

class PwCartRecs extends HTMLElement {
  connectedCallback() {
    const { productId, sectionId, limit, url } = this.dataset;
    if (!productId || !sectionId || !url) return;
    const requestUrl = `${url}?section_id=${encodeURIComponent(sectionId)}&product_id=${encodeURIComponent(
      productId
    )}&limit=${encodeURIComponent(limit || 3)}&intent=related`;
    const cached = recsCache.get(requestUrl);
    if (cached) {
      this.#apply(cached);
      return;
    }
    fetch(requestUrl)
      .then((response) => (response.ok ? response.text() : ''))
      .then((html) => {
        if (!html) return;
        recsCache.set(requestUrl, html);
        this.#apply(html);
      })
      .catch(() => {});
  }

  #apply(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('pw-cart-recs');
    const inner = fresh?.querySelector('.pw-recs__inner');
    if (!fresh || !inner || fresh.dataset.source !== 'related') return;
    if (!inner.querySelector('.pw-recs__item, .pw-recs__card')) return;
    const current = this.querySelector('.pw-recs__inner');
    if (current) current.replaceWith(inner);
    this.dataset.source = 'related';
  }
}

if (!customElements.get('pw-cart-recs')) {
  customElements.define('pw-cart-recs', PwCartRecs);
}

class PwCartSticky extends HTMLElement {
  #observer = null;

  connectedCallback() {
    const target = document.getElementById('checkout');
    if (!target) return;
    this.#observer = new IntersectionObserver(
      (entries) => {
        const visible = entries[0]?.isIntersecting;
        this.toggleAttribute('data-visible', !visible);
      },
      { threshold: 0.2 }
    );
    this.#observer.observe(target);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }
}

if (!customElements.get('pw-cart-sticky')) {
  customElements.define('pw-cart-sticky', PwCartSticky);
}

/* ---------------------------------------------------------------------------------------------
 * 4. Build Your Own Clicker helper-line reconciliation.
 *
 * The clicker builder adds hidden R0 cap lines (line property `_byo_cap`) next to each kit so cap
 * stock decrements. They are not tied to the kit by Shopify, so removing a kit, or changing its
 * quantity, used to leave those lines behind: they still reached checkout and still took cap
 * stock. After every cart change this sets each hidden cap line to exactly what the cart's kits
 * and "Extra Cap" charge lines need, and removes it when nothing needs it.
 *
 * The builder itself (shapes, caps, pricing, what it adds to the cart) is untouched. Only hidden
 * R0 lines are ever changed, never a line a customer can see or is charged for. Anything that
 * cannot be matched with certainty is left exactly as it is.
 * ------------------------------------------------------------------------------------------- */

const BYO_PROP = '_byo_cap';
const BYO_KIND_BUILD = 'Build Your Own Clicker';
const BYO_KIND_EXTRA = 'Extra cap';
const BYO_KIT_TYPE = 'Clicker Kit';
const BYO_CHARGE_TYPE = 'Clicker Cap Charge';
const BYO_SOURCE = 'pw-byo-reconcile';
const BYO_FLAG = 'pw-byo-helpers';

let byoBusy = false;
let byoQueued = false;

const byoRoutes = () => {
  const r = typeof Theme !== 'undefined' && Theme?.routes ? Theme.routes : {};
  return { cart: r.cart_url || '/cart', update: r.cart_update_url || '/cart/update.js' };
};

/** Labels a hidden cap line would carry on a kit ("Blue Wave", plain "Red") and as an extra ("Wave Blue"). */
function byoCapLabels(item) {
  const values = (item.options_with_values || []).map((option) => String(option.value || '').trim());
  if (values.length === 2 && values[0] && values[1]) {
    const [texture, colour] = values;
    return { build: texture === 'Plain' ? colour : `${colour} ${texture}`, extra: `${texture} ${colour}` };
  }
  if (values.length === 1 && values[0]) return { build: values[0], extra: values[0] };
  return null;
}

/** @returns {Record<string, number>} line key -> corrected quantity, only for lines that are wrong */
export function byoHelperCorrections(items) {
  const updates = {};
  if (!Array.isArray(items)) return updates;

  const helpers = items.filter((item) => item?.properties && item.properties[BYO_PROP]);
  if (!helpers.length) return updates;

  const need = { [BYO_KIND_BUILD]: {}, [BYO_KIND_EXTRA]: {} };
  for (const item of items) {
    if (!item || (item.properties && item.properties[BYO_PROP])) continue;
    const quantity = Number(item.quantity) || 0;
    if (item.product_type === BYO_KIT_TYPE) {
      for (const [name, value] of Object.entries(item.properties || {})) {
        if (!/^Cap \d+$/.test(name) || !value) continue;
        need[BYO_KIND_BUILD][value] = (need[BYO_KIND_BUILD][value] || 0) + quantity;
      }
    } else if (item.product_type === BYO_CHARGE_TYPE && item.properties?.Cap) {
      const label = item.properties.Cap;
      need[BYO_KIND_EXTRA][label] = (need[BYO_KIND_EXTRA][label] || 0) + quantity;
    }
  }

  for (const helper of helpers) {
    const kind = helper.properties[BYO_PROP];
    const labels = byoCapLabels(helper);
    if (!labels || !need[kind]) continue; // unknown shape: never guess
    const label = kind === BYO_KIND_EXTRA ? labels.extra : labels.build;
    const wanted = need[kind][label] || 0;
    need[kind][label] = 0; // a label is served by one helper line; any duplicate line is surplus
    if (wanted !== helper.quantity) updates[helper.key] = wanted;
  }
  return updates;
}

async function byoReconcile() {
  if (byoBusy) {
    byoQueued = true;
    return;
  }
  byoBusy = true;
  try {
    const { cart, update } = byoRoutes();
    const current = await fetch(`${cart}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    const hasHelpers = (current.items || []).some((item) => item?.properties && item.properties[BYO_PROP]);
    try {
      if (hasHelpers) sessionStorage.setItem(BYO_FLAG, '1');
      else sessionStorage.removeItem(BYO_FLAG);
    } catch (_) {}

    let updates = byoHelperCorrections(current.items);
    if (!Object.keys(updates).length) return;

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean);
    const post = (body) =>
      fetch(update, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: body, sections: sectionIds.join(','), sections_url: window.location.pathname }),
      }).then((r) => r.json());

    let result = await post(updates);
    if (result.status || result.errors) {
      // Raising a helper line can be refused when a cap is out of stock. Removing surplus is what
      // protects inventory, so retry with the reductions only.
      const byKey = Object.fromEntries((current.items || []).map((item) => [item.key, item.quantity]));
      updates = Object.fromEntries(Object.entries(updates).filter(([key, quantity]) => quantity < byKey[key]));
      if (!Object.keys(updates).length) return;
      result = await post(updates);
      if (result.status || result.errors) return;
    }

    // Let the drawer, cart page and header re-render exactly as they do for any other cart change.
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [], promise: deferred.promise })
    );
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(result),
      detail: { items: result.items, sections: result.sections, source: BYO_SOURCE, sourceId: BYO_SOURCE, didError: false },
    });
  } catch (_) {
    // Never let a cleanup problem affect the cart the customer is using.
  } finally {
    byoBusy = false;
    if (byoQueued) {
      byoQueued = false;
      byoReconcile();
    }
  }
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const settle = (detail) => {
    if (detail?.source === BYO_SOURCE || detail?.didError) return;
    const items = detail?.items;
    // Skip the extra request when the event already shows a cart with no clicker lines at all.
    if (Array.isArray(items) && !items.some((item) => item?.product_type === BYO_KIT_TYPE || item?.properties?.[BYO_PROP])) {
      let flagged = false;
      try {
        flagged = sessionStorage.getItem(BYO_FLAG) === '1';
      } catch (_) {}
      if (!flagged) return;
    }
    byoReconcile();
  };
  if (event.promise) event.promise.then(({ detail }) => settle(detail)).catch(() => {});
  else settle(undefined);
});

// Carts that already hold stale helper lines (older sessions, or a tab closed mid-change).
{
  let flagged = false;
  try {
    flagged = sessionStorage.getItem(BYO_FLAG) === '1';
  } catch (_) {}
  const onCartPage = window.location.pathname.replace(/\/$/, '').endsWith('/cart');
  if (flagged || onCartPage) {
    const run = () => byoReconcile();
    if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 2000 });
    else setTimeout(run, 600);
  }
}
